import { FC } from 'react';
import { HiOutlinePencilAlt, HiOutlineTrash, HiOutlineExternalLink } from 'react-icons/hi';
import { DAV_MAIN_DOMAIN } from '../../../../constants';
import { Card } from '../../../ui/Card';

interface AccommodationCardProps {
  accommodation: any;
  layout?: 'grid' | 'list';
  onEditHandler: () => void;
  onDeleteHandler: () => void;
}

const AccommodationCard: FC<AccommodationCardProps> = ({ accommodation, layout = 'grid', onEditHandler, onDeleteHandler }) => {
  const isList = layout === 'list';

  return (
    <Card
      variant='glass'
      hoverable
      className={`
        group overflow-hidden border-none shadow-lg shadow-gray-200/50 dark:shadow-none
        transition-all duration-300
        ${isList ? 'h-full sm:h-56' : 'h-full flex flex-col'}
      `}
    >
      <div className={`flex flex-col ${isList ? 'sm:flex-row h-full' : 'h-full'}`}>
        <div className={`relative overflow-hidden ${isList ? 'w-full sm:w-72 h-48 sm:h-full flex-shrink-0' : 'aspect-[16/10]'}`}>
          {accommodation.image ? (
            <img
              src={accommodation.image}
              alt={accommodation.name}
              className='w-full h-full object-cover transition-transform duration-700 group-hover:scale-110'
            />
          ) : (
            <div className='w-full h-full bg-gradient-to-br from-amber-100 to-orange-200 dark:from-amber-900/30 dark:to-orange-800/30 flex items-center justify-center'>
              <span className='text-4xl text-amber-400'>🏨</span>
            </div>
          )}
          <div className='absolute inset-0 bg-gradient-to-t from-black/80 via-black/20 to-transparent opacity-80 group-hover:opacity-100 transition-opacity duration-500' />
          <div className='absolute bottom-4 left-4 right-4 text-white'>
            <h3 className='text-lg font-bold drop-shadow-[0_2px_4px_rgba(0,0,0,0.8)]'>
              {accommodation.name}
            </h3>
          </div>
        </div>

        <div className='flex flex-col flex-1 p-5 bg-white/50 dark:bg-gray-800/10 min-w-0'>
          <div className='space-y-1 mb-4'>
            {accommodation.type && (
              <span className='inline-block text-xs font-semibold px-2.5 py-1 rounded-full bg-amber-100 dark:bg-amber-900/30 text-amber-700 dark:text-amber-400 capitalize'>
                {accommodation.type}
              </span>
            )}
            {accommodation.location && (
              <p className='text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider mt-1'>
                {accommodation.location}
              </p>
            )}
            {accommodation.country?.name && (
              <p className='text-sm text-gray-600 dark:text-gray-400'>
                {accommodation.country.name}
              </p>
            )}
          </div>

          <div className='flex items-center justify-between pt-4 border-t border-gray-100 dark:border-gray-700/50 mt-auto'>
            <div className='flex items-center gap-2'>
              <button
                type='button'
                onClick={(e) => { e.stopPropagation(); onEditHandler(); }}
                className='p-2.5 rounded-xl bg-gray-50 dark:bg-gray-800 text-gray-600 dark:text-gray-400 hover:bg-primary-50 hover:text-primary-600 dark:hover:bg-primary-900/20 dark:hover:text-primary-400 transition-all border border-transparent hover:border-primary-200'
                title='Edit Accommodation'
              >
                <HiOutlinePencilAlt className='w-5 h-5' />
              </button>
              <button
                type='button'
                onClick={(e) => { e.stopPropagation(); onDeleteHandler(); }}
                className='p-2.5 rounded-xl bg-rose-50 dark:bg-rose-900/10 text-rose-600 dark:text-rose-400 hover:bg-rose-100 hover:text-rose-700 dark:hover:bg-rose-900/30 transition-all border border-transparent hover:border-rose-200'
                title='Delete Accommodation'
              >
                <HiOutlineTrash className='w-5 h-5' />
              </button>
            </div>
            {accommodation.slug && (
              <a
                href={`${DAV_MAIN_DOMAIN}/accommodations/${accommodation.slug}`}
                target='_blank'
                rel='noopener noreferrer'
                className='flex items-center gap-1.5 text-xs font-semibold text-primary-600 dark:text-primary-400 hover:underline'
                title='View on website'
              >
                View on site
                <HiOutlineExternalLink className='w-4 h-4' />
              </a>
            )}
          </div>
        </div>
      </div>
    </Card>
  );
};

export default AccommodationCard;
